/**
 * ImageManagerModal — Full image management for procurement item rows
 *
 * Features:
 *   • Upload from file (drag & drop or browse)
 *   • Crop tool (canvas-based drag rectangle)
 *   • Attach by URL link
 *   • Rotate (90° steps)
 *   • Multiple image sizes auto-generated: Thumb 64px / Small 300px / Large 800px
 *   • Gallery view for multiple images per item
 *   • localStorage persistence via imageStorage.js
 *
 * Usage:
 *   <ImageManagerModal
 *     itemKey="1127000206"       — unique key for localStorage (use itemCode)
 *     itemLabel="Printed Saree"  — shown in modal title
 *     onSave={ref => updRow(idx, '_imgRef', ref)}  — receives DB ref object
 *     onClose={() => setImgModal(null)}
 *   />
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { loadImages, saveImages, generateSizes, cropImage, rotateImage } from '../../lib/imageStorage';

const TABS = ['Upload', 'Crop', 'Link', 'Gallery'];

// ─── Simple canvas crop tool ────────────────────────────────────────────────
function CropTool({ src, onCropped, onCancel }) {
  const canvasRef = useRef(null);
  const [imgDims, setImgDims]   = useState({ w: 0, h: 0 });
  const [dragging, setDragging] = useState(false);
  const [rect, setRect]         = useState({ x: 0, y: 0, w: 0, h: 0 });
  const [start, setStart]       = useState({ x: 0, y: 0 });
  const [rotation, setRotation] = useState(0);
  const [rotatedSrc, setRotSrc] = useState(src);

  // Draw image + crop rect on canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.onload = () => {
      const MAX = 520;
      const scale = Math.min(MAX / img.naturalWidth, MAX / img.naturalHeight, 1);
      const dw = Math.round(img.naturalWidth * scale);
      const dh = Math.round(img.naturalHeight * scale);
      canvas.width  = dw;
      canvas.height = dh;
      setImgDims({ w: dw, h: dh });
      ctx.drawImage(img, 0, 0, dw, dh);
      // Draw crop rect overlay
      if (rect.w > 0 && rect.h > 0) {
        ctx.fillStyle = 'rgba(0,0,0,0.4)';
        ctx.fillRect(0, 0, dw, dh);
        ctx.clearRect(rect.x, rect.y, Math.abs(rect.w), Math.abs(rect.h));
        ctx.strokeStyle = '#4fa3ff';
        ctx.lineWidth = 2;
        ctx.strokeRect(rect.x, rect.y, Math.abs(rect.w), Math.abs(rect.h));
      }
    };
    img.src = rotatedSrc;
  }, [rotatedSrc, rect]);

  function relPos(e) {
    const r = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  }
  function onMouseDown(e) { const p = relPos(e); setStart(p); setDragging(true); setRect({...p, w:0, h:0}); }
  function onMouseMove(e) {
    if (!dragging) return;
    const p = relPos(e);
    setRect({ x: start.x, y: start.y, w: p.x - start.x, h: p.y - start.y });
  }
  function onMouseUp() { setDragging(false); }

  async function applyCrop() {
    if (!rect.w || !rect.h) { onCropped(rotatedSrc); return; }
    const normRect = {
      x: rect.w < 0 ? rect.x + rect.w : rect.x,
      y: rect.h < 0 ? rect.y + rect.h : rect.y,
      w: Math.abs(rect.w),
      h: Math.abs(rect.h),
    };
    const cropped = await cropImage(rotatedSrc, normRect, imgDims);
    onCropped(cropped);
  }

  async function handleRotate() {
    const newRot = (rotation + 90) % 360;
    setRotation(newRot);
    const rotated = await rotateImage(rotatedSrc, 90);
    setRotSrc(rotated);
    setRect({ x: 0, y: 0, w: 0, h: 0 });
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
      <div style={{ fontSize: 11, color: 'var(--pi2-ink-soft)', marginBottom: 4 }}>
        Click and drag to select crop area. Leave blank to use full image.
      </div>
      <canvas ref={canvasRef} style={{ border: '1px solid var(--pi2-border)', cursor: 'crosshair', maxWidth: '100%' }}
        onMouseDown={onMouseDown} onMouseMove={onMouseMove} onMouseUp={onMouseUp}/>
      <div style={{ display: 'flex', gap: 8 }}>
        <button className="pi2-btn" type="button" onClick={handleRotate}>↺ Rotate 90°</button>
        <button className="pi2-btn" type="button" onClick={() => setRect({x:0,y:0,w:0,h:0})}>✕ Clear Crop</button>
        <button className="pi2-btn primary" type="button" onClick={applyCrop}>✓ Apply Crop</button>
        <button className="pi2-btn" type="button" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

// ─── Main Modal ─────────────────────────────────────────────────────────────
export default function ImageManagerModal({ itemKey, itemLabel = 'Item', onSave, onClose }) {
  const [tab,      setTab]      = useState('Upload');
  const [images,   setImages]   = useState(() => loadImages(itemKey));
  const [preview,  setPreview]  = useState(null);  // current working image (base64)
  const [linkUrl,  setLinkUrl]  = useState('');
  const [linkErr,  setLinkErr]  = useState('');
  const [selIdx,   setSelIdx]   = useState(null);
  const [saving,   setSaving]   = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);

  // Load file from input/drop
  function readFile(file) {
    if (!file || !file.type.startsWith('image/')) return;
    if (file.size > 8 * 1024 * 1024) { alert('Image too large (max 8 MB)'); return; }
    const reader = new FileReader();
    reader.onload = ev => { setPreview(ev.target.result); setTab('Crop'); };
    reader.readAsDataURL(file);
  }

  // After crop/confirm → generate sizes → add to gallery
  async function handleImageReady(src) {
    setSaving(true);
    const sizes = await generateSizes(src);
    const newImg = {
      id:        Date.now(),
      name:      `Image ${images.length + 1}`,
      thumb:     sizes.thumb,
      small:     sizes.small,
      large:     sizes.large,
      timestamp: Date.now(),
    };
    const updated = [...images, newImg];
    setImages(updated);
    setPreview(null);
    setTab('Gallery');
    setSaving(false);
  }

  // Load from URL
  async function handleLoadUrl() {
    if (!linkUrl.trim()) return;
    setLinkErr('');
    try {
      // Try to create an image from URL
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth; canvas.height = img.naturalHeight;
        canvas.getContext('2d').drawImage(img, 0, 0);
        try {
          const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
          setPreview(dataUrl); setTab('Crop');
        } catch {
          // CORS issue — store URL directly without canvas
          handleImageReady(linkUrl);
        }
      };
      img.onerror = () => setLinkErr('Could not load image from URL. Check the URL and try again.');
      img.src = linkUrl;
    } catch (e) {
      setLinkErr('Invalid URL');
    }
  }

  function deleteImage(idx) {
    const updated = images.filter((_, i) => i !== idx);
    setImages(updated);
    if (selIdx === idx) setSelIdx(null);
  }

  function handleSave() {
    const ref = saveImages(itemKey, images);
    onSave && onSave(ref, images);
    onClose();
  }

  const selectedImg = selIdx !== null ? images[selIdx] : null;

  return (
    <>
      <div className="pi2-overlay-bg" onClick={onClose}/>
      <div className="pi2-modal-wrap">
        <div className="pi2-modal" style={{ width: 820, maxWidth: '97vw' }}>

          <div className="pi2-modal-head">
            <div className="t">
              <small>Image Manager · {itemKey}</small>
              {itemLabel}
            </div>
            <button className="x" onClick={onClose}>✕</button>
          </div>

          {/* Tab bar */}
          <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid var(--pi2-border)', background: 'var(--pi2-surface-alt)' }}>
            {TABS.map(t => (
              <button key={t} type="button"
                onClick={() => setTab(t)}
                style={{
                  padding: '8px 20px', border: 'none', background: 'none',
                  borderBottom: tab === t ? '2px solid var(--pi2-accent2)' : '2px solid transparent',
                  fontWeight: tab === t ? 700 : 400, fontSize: 12,
                  color: tab === t ? 'var(--pi2-accent)' : 'var(--pi2-ink-soft)',
                  cursor: 'pointer',
                }}>
                {t === 'Gallery' ? `Gallery (${images.length})` : t}
              </button>
            ))}
          </div>

          <div className="pi2-modal-body" style={{ minHeight: 320, padding: 12 }}>

            {/* ── UPLOAD tab ── */}
            {tab === 'Upload' && (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, padding: '20px 0' }}>
                <div
                  data-testid="img-drop-zone"
                  onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={e => { e.preventDefault(); setDragOver(false); readFile(e.dataTransfer.files[0]); }}
                  onClick={() => fileRef.current?.click()}
                  style={{
                    width: 360, height: 200, border: `2px dashed ${dragOver ? 'var(--pi2-accent2)' : 'var(--pi2-border)'}`,
                    borderRadius: 8, display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center', gap: 10,
                    cursor: 'pointer', background: dragOver ? 'var(--pi2-accent-soft)' : 'var(--pi2-surface)',
                    transition: 'all .15s',
                  }}>
                  <div style={{ fontSize: 42, opacity: 0.4 }}>📷</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--pi2-ink)' }}>
                    Drop image here or click to browse
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--pi2-ink-soft)' }}>
                    PNG, JPG, WEBP — max 8 MB
                  </div>
                </div>
                <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }}
                  onChange={e => readFile(e.target.files[0])} />
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="pi2-btn primary" type="button" onClick={() => fileRef.current?.click()}>
                    📁 Browse Files
                  </button>
                  <button className="pi2-btn" type="button" onClick={() => { setLinkUrl(''); setTab('Link'); }}>
                    🔗 Use URL Link
                  </button>
                </div>
              </div>
            )}

            {/* ── CROP tab ── */}
            {tab === 'Crop' && preview && (
              <CropTool src={preview} onCropped={handleImageReady} onCancel={() => { setPreview(null); setTab('Upload'); }} />
            )}
            {tab === 'Crop' && !preview && (
              <div style={{ padding: 40, textAlign: 'center', color: 'var(--pi2-ink-soft)' }}>
                Upload or link an image first to use the crop tool.
                <br/><button className="pi2-btn" style={{ marginTop: 12 }} onClick={() => setTab('Upload')}>← Upload Image</button>
              </div>
            )}

            {/* ── LINK tab ── */}
            {tab === 'Link' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: '16px 0', maxWidth: 560, margin: '0 auto' }}>
                <div style={{ fontSize: 12, color: 'var(--pi2-ink-soft)' }}>
                  Paste a direct image URL (JPG, PNG, or WEBP). The image will be loaded and saved locally.
                </div>
                <div className="pi2-field">
                  <label>Image URL</label>
                  <div className="pi2-autoinput">
                    <input value={linkUrl} placeholder="https://example.com/item.jpg"
                      onChange={e => { setLinkUrl(e.target.value); setLinkErr(''); }} />
                    <button className="pi2-lkpbtn" type="button" onClick={handleLoadUrl}>Load</button>
                  </div>
                  {linkErr && <span style={{ fontSize: 11, color: '#c0392b' }}>{linkErr}</span>}
                </div>
                <button className="pi2-btn primary" type="button" onClick={handleLoadUrl} style={{ alignSelf: 'flex-start' }}>
                  ↓ Load &amp; Preview
                </button>
              </div>
            )}

            {/* ── GALLERY tab ── */}
            {tab === 'Gallery' && (
              <div>
                {images.length === 0 ? (
                  <div style={{ padding: 40, textAlign: 'center', color: 'var(--pi2-ink-soft)' }}>
                    No images attached yet.
                    <br/><button className="pi2-btn primary" style={{ marginTop: 12 }} onClick={() => setTab('Upload')}>+ Add Image</button>
                  </div>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    {/* Thumbnail grid */}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignContent: 'flex-start' }}>
                      {images.map((img, idx) => (
                        <div key={img.id} data-testid={`img-thumb-${idx}`}
                          onClick={() => setSelIdx(idx)}
                          style={{
                            position: 'relative', cursor: 'pointer',
                            border: `2px solid ${selIdx === idx ? 'var(--pi2-accent2)' : 'var(--pi2-border)'}`,
                            borderRadius: 4, overflow: 'hidden', width: 80, height: 80,
                          }}>
                          <img src={img.thumb} alt={img.name}
                            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                          <button type="button"
                            onClick={e => { e.stopPropagation(); deleteImage(idx); }}
                            style={{ position: 'absolute', top: 2, right: 2, background: 'rgba(0,0,0,.55)', color: '#fff', border: 'none', borderRadius: '50%', width: 18, height: 18, fontSize: 10, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            ✕
                          </button>
                          {idx === 0 && <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(26,86,167,.8)', color: '#fff', fontSize: 9, textAlign: 'center', fontWeight: 700 }}>PRIMARY</div>}
                        </div>
                      ))}
                      <button type="button" className="pi2-addfld" style={{ width: 80, height: 80, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4, borderRadius: 4 }}
                        onClick={() => setTab('Upload')}>
                        <span style={{ fontSize: 22 }}>+</span>
                        <span style={{ fontSize: 10 }}>Add</span>
                      </button>
                    </div>

                    {/* Selected image detail */}
                    <div>
                      {selectedImg ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                          <img src={selectedImg.small} alt={selectedImg.name}
                            style={{ width: '100%', maxHeight: 220, objectFit: 'contain', border: '1px solid var(--pi2-border)', borderRadius: 4, background: '#f8f9fb' }} />
                          <div style={{ fontSize: 11, color: 'var(--pi2-ink-soft)' }}>
                            {selectedImg.name} · Added {new Date(selectedImg.timestamp).toLocaleDateString('en-IN')}
                          </div>
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            {['thumb','small','large'].map(sz => (
                              <a key={sz} href={selectedImg[sz]} download={`item_${sz}.jpg`}
                                className="pi2-btn" style={{ fontSize: 10, textDecoration: 'none' }}>
                                ↓ {sz.charAt(0).toUpperCase() + sz.slice(1)}
                              </a>
                            ))}
                            <button type="button" className="pi2-btn" style={{ fontSize: 10 }}
                              onClick={() => { setPreview(selectedImg.large); setTab('Crop'); }}>
                              ✂ Re-Crop
                            </button>
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 6, marginTop: 4 }}>
                            {[['Thumbnail','thumb','64×64'],['Small','small','300×300'],['Large','large','800px']].map(([lbl,k,dim])=>(
                              <div key={k} style={{ textAlign:'center', background:'var(--pi2-surface-alt)', borderRadius:4, padding:'4px' }}>
                                <img src={selectedImg[k]} alt={lbl} style={{ width:48, height:48, objectFit:'cover', borderRadius:3, display:'block', margin:'0 auto 4px' }}/>
                                <div style={{ fontSize:10, fontWeight:700 }}>{lbl}</div>
                                <div style={{ fontSize:9, color:'var(--pi2-ink-soft)' }}>{dim}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div style={{ padding:20, textAlign:'center', color:'var(--pi2-ink-soft)', fontSize:12 }}>
                          Click a thumbnail to preview
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {saving && (
            <div style={{ padding: '6px 12px', background: '#dbeafe', fontSize: 12, color: '#1d4ed8' }}>
              ⏳ Generating image sizes (thumb / small / large)…
            </div>
          )}

          <div className="pi2-modal-foot">
            <div style={{ fontSize: 11, color: 'var(--pi2-ink-soft)' }}>
              {images.length} image{images.length !== 1 ? 's' : ''} · Stored in localStorage · Key: <code>{itemKey}</code>
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button className="pi2-btn" type="button" onClick={onClose}>Cancel</button>
              <button className="pi2-btn primary" type="button" onClick={handleSave} data-testid="img-save-btn"
                disabled={saving}>
                Save Images ({images.length})
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
